import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import api from "@/api/client";
import { Card, Spinner } from "@/components/ui";
import { useAuthStore } from "@/store/authStore";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import clsx from "clsx";
import toast from "react-hot-toast";
import { normalizeIdentity } from "@/lib/studox-auth/index.js";
import { launchMeet } from "@/utils/launchMeet";

/* ── StudoX Meet launcher — one-click into the live comms hub (SSO handoff) ── */
function MeetLaunchCard() {
  const [busy, setBusy] = useState(false);
  const open = async () => { if (busy) return; setBusy(true); await launchMeet(); };
  return (
    <button
      onClick={open}
      disabled={busy}
      className="w-full text-left rounded-2xl p-4 border transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.99] relative overflow-hidden group"
      style={{
        background: "linear-gradient(135deg, rgba(124,58,237,0.18), rgba(34,211,238,0.10))",
        borderColor: "rgba(124,58,237,0.4)",
        boxShadow: "0 0 26px rgba(124,58,237,0.14)",
      }}
    >
      {/* sheen */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: "linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.08) 50%, transparent 60%)" }} />
      <div className="relative flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl grid place-items-center flex-shrink-0"
          style={{ background: "linear-gradient(135deg, #7c3aed, #22d3ee)", boxShadow: "0 4px 14px rgba(124,58,237,0.4)" }}>
          <span className="text-xl">🎧</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-black text-white text-sm">StudoX Meet</p>
            <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-md"
              style={{ background: "rgba(34,211,238,0.18)", color: "#67e8f9", border: "1px solid rgba(34,211,238,0.35)" }}>Live</span>
          </div>
          <p className="text-[11px] mt-0.5" style={{ color: "#c4b5fd" }}>
            Sprach- & Videoräume, Community-Server — sprich & sieh dein Team.
          </p>
        </div>
        {busy ? (
          <svg className="animate-spin w-4 h-4 text-cyan-300 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="#67e8f9" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 group-hover:translate-x-0.5 transition-transform"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        )}
      </div>
    </button>
  );
}

const ROLE_LABELS = {
  teacher: "Lehrperson",
  mentor: "Mentor",
  parent: "Elternteil",
  student: "Schüler",
  admin: "Administrator",
  therapist: "Therapeut",
  lernbegleiter: "Lernbegleiter",
};

// ── New Conversation Modal ────────────────────────────────────────────────────
function NewConversationModal({ open, onClose }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["message-contacts"],
    queryFn: () => api.get("/messages/contacts", { timeout: 8000 }).then((r) => r.data),
    enabled: open,
    retry: 1,
    staleTime: 30_000,
  });

  const createConv = useMutation({
    mutationFn: (recipientId) => api.post("/messages/conversations", { recipientId }).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["conversations"] });
      onClose();
      navigate(`/messages/${data.conversationId}`);
    },
    onError: (err) => toast.error(err.response?.data?.error || "Konnte Gespräch nicht starten"),
  });

  if (!open) return null;

  const contacts = data?.contacts || [];
  const filtered = contacts.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-sm bg-nexus-sf border border-nexus-border rounded-3xl overflow-hidden shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-nexus-border">
          <h2 className="font-black text-base">💬 Neue Nachricht</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/5 transition-all"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-nexus-border">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Person suchen..."
            autoFocus
            className="w-full bg-nexus-s2 border border-nexus-border rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors"
          />
        </div>

        {/* Contact list */}
        <div className="overflow-y-auto max-h-80 p-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
            </div>
          ) : isError ? (
            <div className="text-center py-8 text-gray-500 text-sm">Kontakte konnten nicht geladen werden</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-sm">Keine Kontakte gefunden</p>
              <p className="text-gray-700 text-xs mt-1">Verbinde dich zuerst mit jemandem</p>
            </div>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                onClick={() => createConv.mutate(c.id)}
                disabled={createConv.isPending}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 transition-colors text-left active:scale-[0.98]"
              >
                <div className="w-10 h-10 rounded-xl bg-nexus-s2 flex items-center justify-center text-lg border border-nexus-border flex-shrink-0">
                  {c.avatar || "👤"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-white truncate">{c.name}</p>
                  <p className="text-[10px] text-gray-500">{ROLE_LABELS[c.role] || c.role}</p>
                </div>
                {createConv.isPending && createConv.variables === c.id ? (
                  <div className="w-4 h-4 border-2 border-violet-500/30 border-t-violet-400 rounded-full animate-spin flex-shrink-0" />
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#6b7280" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
                )}
              </button>
            ))
          )}
        </div>

        <div className="px-4 pb-4">
          <p className="text-[10px] text-center text-gray-700">
            Nur Personen die mit dir verbunden sind werden hier angezeigt
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Conversation List ─────────────────────────────────────────────────────────
function ConversationList() {
  const navigate = useNavigate();
  const [newConvOpen, setNewConvOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["conversations"],
    queryFn: () => api.get("/messages/conversations", { timeout: 10000 }).then((r) => r.data),
    refetchInterval: 15_000,
    retry: 1,
    retryDelay: 1000,
    staleTime: 5_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-5 animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="h-7 w-40 bg-nexus-s2 rounded-xl animate-pulse" />
          <div className="h-9 w-32 bg-nexus-s2 rounded-xl animate-pulse" />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-nexus-sf border border-nexus-border rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-5 animate-fade-in">
        <h1 className="text-xl font-black">💬 Nachrichten</h1>
        <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-red-950/30 border border-red-900/30 flex items-center justify-center text-3xl">⚠️</div>
          <div>
            <p className="font-black text-white">Nachrichten konnten nicht geladen werden</p>
            <p className="text-xs text-gray-500 mt-1">Überprüfe deine Verbindung</p>
          </div>
          <button
            onClick={() => refetch()}
            className="px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-bold transition-all active:scale-95"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  const convos = data?.conversations || [];

  return (
    <>
      <div className="space-y-4 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-black">💬 Nachrichten</h1>
          <button
            onClick={() => setNewConvOpen(true)}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-xs font-black transition-all active:scale-95"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Neue Nachricht
          </button>
        </div>

        {/* StudoX Meet — the live comms hub, one click away */}
        <MeetLaunchCard />

        {convos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
            <div className="w-20 h-20 rounded-3xl flex items-center justify-center text-4xl"
              style={{
                background: "linear-gradient(135deg, rgba(139,92,246,0.12), rgba(6,182,212,0.08))",
                border: "1px solid rgba(139,92,246,0.2)",
              }}
            >
              💬
            </div>
            <div>
              <p className="font-black text-white text-lg">Noch keine Nachrichten</p>
              <p className="text-sm text-gray-500 mt-1">Schreib deinem Lehrer, Elternteil oder Mentor</p>
            </div>
            <button
              onClick={() => setNewConvOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-black text-sm transition-all active:scale-95"
              style={{
                background: "linear-gradient(135deg, rgba(139,92,246,0.2), rgba(6,182,212,0.12))",
                border: "1px solid rgba(139,92,246,0.35)",
                color: "#c4b5fd",
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              Gespräch starten
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {convos.map((c) => {
              const other = c.participants?.find((p) => p.userId !== c.myId)?.user;
              const last = c.messages?.[0];
              return (
                <button
                  key={c.id}
                  onClick={() => navigate(`/messages/${c.id}`)}
                  className={clsx(
                    "w-full text-left bg-nexus-sf border rounded-2xl p-4 transition-all hover:bg-nexus-s2 active:scale-[0.99]",
                    c.unreadCount > 0 ? "border-violet-500/40" : "border-nexus-border"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-nexus-s2 flex items-center justify-center text-xl border border-nexus-border flex-shrink-0 relative">
                      {other?.avatar || "👤"}
                      {c.unreadCount > 0 && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-violet-600 flex items-center justify-center">
                          <span className="text-[9px] font-black text-white">{c.unreadCount > 9 ? "9+" : c.unreadCount}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={clsx("font-black text-sm truncate flex items-center gap-1", c.unreadCount > 0 ? "text-white" : "text-gray-200")}>
                          {other?.name || c.title || "Gespräch"}
                          {other && normalizeIdentity(other)?.verified && <span title="Verifiziert" style={{ color: "#22d3ee", fontSize: 11 }}>✓</span>}
                        </p>
                        {last && (
                          <span className="text-[10px] text-gray-600 flex-shrink-0">
                            {formatDistanceToNow(new Date(last.createdAt), { addSuffix: false, locale: de })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-violet-400/60 font-medium">{ROLE_LABELS[other?.role] || other?.role}</span>
                        {last && <span className="text-gray-700">·</span>}
                        <p className={clsx("text-xs truncate flex-1", c.unreadCount > 0 ? "text-gray-300 font-medium" : "text-gray-600")}>
                          {last?.content || "Neue Unterhaltung"}
                        </p>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <NewConversationModal open={newConvOpen} onClose={() => setNewConvOpen(false)} />
    </>
  );
}

// ── Chat View ─────────────────────────────────────────────────────────────────
function ChatView({ convId }) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const bottomRef = useRef();

  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ["messages", convId],
    queryFn: ({ pageParam }) =>
      api.get(`/messages/conversations/${convId}${pageParam ? `?before=${pageParam}` : ""}`, { timeout: 10000 }).then((r) => r.data),
    getNextPageParam: (last) => last.nextCursor || undefined,
    initialPageParam: null,
    refetchInterval: 5_000,
    retry: 1,
    retryDelay: 1000,
    staleTime: 3_000,
  });

  const send = useMutation({
    mutationFn: () => api.post(`/messages/conversations/${convId}/send`, { content: text }),
    onSuccess: () => {
      setText("");
      qc.invalidateQueries({ queryKey: ["messages", convId] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: (err) => toast.error(err.response?.data?.error || "Nachricht konnte nicht gesendet werden"),
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex flex-col h-[calc(100dvh-7rem)]">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-5 h-5 bg-nexus-s2 rounded animate-pulse" />
          <div className="w-9 h-9 bg-nexus-s2 rounded-xl animate-pulse" />
          <div className="space-y-1.5">
            <div className="h-4 w-24 bg-nexus-s2 rounded animate-pulse" />
            <div className="h-3 w-16 bg-nexus-s2 rounded animate-pulse" />
          </div>
        </div>
        <div className="flex-1 space-y-3 pt-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={clsx("flex", i % 2 === 0 ? "justify-end" : "justify-start")}>
              <div className={clsx("h-10 rounded-2xl animate-pulse bg-nexus-s2", i % 2 === 0 ? "w-40" : "w-52")} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col h-[calc(100dvh-7rem)] items-center justify-center gap-4">
        <div className="text-4xl">⚠️</div>
        <p className="text-white font-black">Nachrichten konnten nicht geladen werden</p>
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/messages")}
            className="px-4 py-2 rounded-xl bg-nexus-s2 border border-nexus-border text-sm font-bold text-gray-400 transition-all hover:text-white"
          >
            Zurück
          </button>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-bold transition-all active:scale-95"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  const pages = data?.pages || [];
  const msgs = pages.flatMap((p) => p.messages || []).reverse();
  const conv = pages[0]?.conversation;
  const other = conv?.participants?.find((p) => p.userId !== user?.id)?.user;

  return (
    <div className="flex flex-col h-[calc(100dvh-7rem)] animate-fade-in">
      {/* Top bar */}
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-nexus-border/50">
        <button
          onClick={() => navigate("/messages")}
          className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/5 transition-all"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div className="w-9 h-9 rounded-xl bg-nexus-s2 flex items-center justify-center text-lg border border-nexus-border flex-shrink-0">
          {other?.avatar || "👤"}
        </div>
        {(() => {
          const oi = other ? normalizeIdentity(other) : null;
          return (
            <div className="flex-1 min-w-0">
              <p className="font-black text-sm truncate flex items-center gap-1.5">
                {other?.name || conv?.title || "Gespräch"}
                {oi?.verified && <span title="Verifiziert von StudoX" style={{ color: "#22d3ee", fontSize: 12 }}>✓</span>}
              </p>
              <p className="text-[10px] font-semibold flex items-center gap-1" style={{ color: oi ? oi.rank.color : "#9ca3af" }}>
                {oi ? <>{oi.rank.glyph} {oi.rank.title}</> : (ROLE_LABELS[other?.role] || other?.role)}
              </p>
            </div>
          );
        })()}
      </div>

      {/* Load older */}
      {hasNextPage && (
        <button
          onClick={() => fetchNextPage()}
          className="text-xs text-violet-400 text-center mb-3 font-bold hover:text-violet-300 transition-colors"
        >
          ↑ Ältere Nachrichten laden
        </button>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-2 scrollbar-hide pr-1">
        {msgs.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
            <p className="text-4xl">👋</p>
            <p className="text-gray-500 text-sm">Noch keine Nachrichten — schreib etwas!</p>
          </div>
        )}
        {msgs.map((m) => {
          const isMe = m.senderId === user?.id;
          return (
            <div key={m.id} className={clsx("flex", isMe ? "justify-end" : "justify-start")}>
              <div
                className={clsx(
                  "max-w-[80%] px-4 py-2.5 rounded-2xl text-sm",
                  isMe
                    ? "bg-violet-600 text-white rounded-br-sm"
                    : "bg-nexus-s2 border border-nexus-border text-gray-200 rounded-bl-sm"
                )}
              >
                <p className="leading-relaxed break-words">{m.content}</p>
                <p className={clsx("text-[10px] mt-1", isMe ? "text-violet-200/70" : "text-gray-500")}>
                  {formatDistanceToNow(new Date(m.createdAt), { addSuffix: true, locale: de })}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="mt-3 flex gap-2 pt-3 border-t border-nexus-border/30">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && text.trim()) {
              e.preventDefault();
              send.mutate();
            }
          }}
          placeholder="Nachricht schreiben..."
          className="flex-1 bg-nexus-s2 border border-nexus-border rounded-2xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors"
        />
        <button
          onClick={() => text.trim() && send.mutate()}
          disabled={!text.trim() || send.isPending}
          className="w-11 h-11 rounded-2xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all active:scale-95"
        >
          {send.isPending ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="white" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Messages() {
  const { convId } = useParams();
  return convId ? <ChatView convId={convId} /> : <ConversationList />;
}
